import { get } from 'lodash';
import {
  fetchActivityList,
  fetchAvailableCouponList,
  fetchBuyMemberCard,
  fetchCouponList,
  fetchGuestList,
  fetchQueryMemberCardPrice,
  fetchActivityOrder,
  fetchUpdateSingleCustomer,
  fetchQuerySalesperson,
  fetchBusinessPaymentType,
  fetchGuestListByKeyword,
  fetchAccountFlow,
  fetchCardInfoByWyuid,
  fetchAllMemberLevelList,
  fetchUserAvailbleCardList,
  fetchCheckinHistoryPageList,
  fetchMemberCardDetail,
  fetchMemberRechargeDetail,
  fetchDouyinCouponList,
  fetchManualCouponList,
} from '@/api/guest';
import { fetchQueryTransactions } from '@/api/transaction';
import { ComponentCode, CouponStatus, ResultComponentCode } from '@/config';
import { renderIntlLabel, uuid } from '@/utils';
import { isNil } from '@/utils/number';
import { getFormatRulesTextFn } from '@/pages/guest/helper';
import actionType from './actionType';

export const clearData = (state) => {
  return {
    type: actionType.CLEAR_DATA,
    payload: state,
  };
};

export const saveData = (state) => {
  return {
    type: actionType.SAVE_DATA,
    payload: state,
  };
};

export const toggleLoading = (state) => {
  return {
    type: actionType.TOGGLE_LOADING,
    payload: state,
  };
};

export const setPagination = (state) => {
  return {
    type: actionType.SET_PAGINATION,
    payload: state,
  };
};

export const resetPagination = (state) => {
  return {
    type: actionType.RESET_PAGINATION,
    payload: state,
  };
};

export const updateIdValidatedRes = (state) => {
  return {
    type: actionType.UPDATE_ID_VALIDATED_RES,
    payload: state,
  };
};

// 会员等级
export const queryAllMemberLevelList = (data) => {
  return async (dispatch) => {
    dispatch(toggleLoading(''));
    try {
      const res = (await fetchAllMemberLevelList(data)) || {};
      if (res.code === 0) {
        const memberLevelList = get(res, 'data') || [];
        const memberLevelMap = memberLevelList?.reduce((pre, current) => {
          const obj = { ...pre };
          if (!isNil(current.level)) {
            obj[current.level] = current;
          }
          return obj;
        }, {});
        dispatch({
          type: actionType.MEMBER_LEVEL_MAP,
          payload: {
            memberLevelMap,
          },
        });
      }
      dispatch(toggleLoading(''));
    } catch (error) {
      dispatch(toggleLoading(''));
    }
  };
};

// 会员充值详情
export const getMemberRechargeDetail = (data: { transactionNo: string }) => {
  return async (dispatch) => {
    dispatch(saveData({ memberRecharge: { memberRechargeDetailLoading: true } }));
    let memberRecharge: any = {};
    try {
      const res = (await fetchMemberRechargeDetail(data)) || {};
      if (res.code === 0) {
        memberRecharge = get(res, 'data', {}) || {};
      }
    } catch (error) {
      //
    } finally {
      dispatch({
        type: actionType.MEMBER_RECHARGE_DETAIL,
        payload: {
          memberRecharge: {
            ...memberRecharge,
            memberRechargeDetailLoading: false,
          },
        },
      });
    }
  };
};

// 会员卡购买详情
export const getMemberCardDetail = (data: { transactionNo: string }) => {
  return async (dispatch) => {
    dispatch(saveData({ memberCard: { memberCardDetailLoading: true } }));
    let memberCard: any = {};
    try {
      const res = (await fetchMemberCardDetail(data)) || {};
      if (res.code === 0) {
        memberCard = get(res, 'data', {}) || {};
      }
    } catch (error) {
      //
    } finally {
      dispatch({
        type: actionType.MEMBER_CARD_DETAIL,
        payload: {
          memberCard: {
            ...memberCard,
            memberCardDetailLoading: false,
          },
        },
      });
    }
  };
};

// 会员入住记录
export const getCheckinHistoryPageList = (data) => {
  return async (dispatch, getState) => {
    let checkinPageList = [];
    const initialState = getState().guest;
    const { checkinHistory } = initialState || {};
    const { pageSize } = checkinHistory;
    let { pageNum, total } = checkinHistory;
    Object.assign(checkinHistory, { checkinPageListLoading: true });
    dispatch(saveData({ checkinHistory }));
    try {
      const res = await fetchCheckinHistoryPageList({ pageNum, pageSize, ...data });
      if (res.code === 0) {
        checkinPageList = get(res, 'data.list', []);
        pageNum = get(res, 'data.pageNum', 1);
        total = get(res, 'data.total', 0);
      }
    } catch (error) {
      pageNum = 1;
      total = 0;
    } finally {
      dispatch({
        type: actionType.CHECKIN_HISTORY_PAGE_LIST,
        payload: {
          checkinHistory: {
            checkinPageList,
            checkinPageListLoading: false,
            pageNum,
            pageSize,
            total,
          },
        },
      });
    }
  };
};

// 查询支付结果
export const queryTransactions = (data: { orderNo: string }) => {
  return async (dispatch) => {
    dispatch(toggleLoading(''));
    try {
      const res = (await fetchQueryTransactions(data)) || {};
      if (res.code === 0) {
        const businessStatus = get(res, 'data.businessStatus', false);
        const paymentStatus = get(res, 'data.paymentStatus', false);
        if (businessStatus && paymentStatus) {
          return Promise.resolve(true);
        }
      }
      dispatch(toggleLoading(''));
      return Promise.reject();
    } catch (error) {
      dispatch(toggleLoading(''));
      return Promise.reject();
    }
  };
};

// 会员流水
export const getAccountFlow = (data) => {
  return async (dispatch) => {
    dispatch(toggleLoading('accountFlowLoading'));
    let accountFlowList = [];
    try {
      const res = await fetchAccountFlow(data);
      if (res?.code === 0) {
        accountFlowList = get(res, 'data', []);
      }
    } catch (error) {
      //
    } finally {
      dispatch(toggleLoading('accountFlowLoading'));
      dispatch({
        type: actionType.ACCOUNT_FLOW_LIST,
        payload: {
          accountFlowList,
        },
      });
    }
  };
};

// 更新脱敏信息
export const updateCardInfo = (state) => {
  return {
    type: actionType.UPDATE_SENSITIZE_BASIC_INFO,
    payload: state,
  };
};

// 宾客详情基础信息
export const getCardInfoByWyuid = ({ desensitizedCardInfo, ...rest }) => {
  return async (dispatch) => {
    dispatch(toggleLoading('cardInfoLoading'));
    let basicCardInfo = {};
    try {
      const res = await fetchCardInfoByWyuid(rest);
      if (res?.code === 0) {
        basicCardInfo = get(res, 'data', {});
        if (Object.keys(desensitizedCardInfo)?.length) {
          const { name, certNo, mobile, email } = desensitizedCardInfo || {};
          basicCardInfo = {
            ...basicCardInfo,
            name,
            certNo,
            mobile,
            email,
          };
        }
      }
    } catch (error) {
      //
    } finally {
      dispatch(toggleLoading('cardInfoLoading'));
      dispatch({
        type: actionType.BASIC_CARD_INFO,
        payload: {
          basicCardInfo,
        },
      });
    }
  };
};

// 更新门店可售用户可购卡状态
export const updateAllGuestCanBuyCard = (guestList) => {
  return async () => {
    const wyuIds = guestList?.filter((item) => item.wyuid)?.map((item) => item.wyuid);
    if (Array.isArray(wyuIds) && wyuIds.length) {
      try {
        const resonse = await fetchUserAvailbleCardList({ wyuIds });
        if (resonse?.code === 0) {
          const canBuyCardMap = resonse?.data;
          let list = [...guestList];
          list = list.map((item) => {
            const tempItem = { ...item };
            const currentList = canBuyCardMap[tempItem.wyuid];
            if (Array.isArray(currentList) && currentList.length) {
              tempItem.canBuyCard = true;
            }
            return tempItem;
          });
          return Promise.resolve(list);
        }
        return Promise.reject();
      } catch (error) {
        return Promise.reject();
      }
    }
    return Promise.reject();
  };
};

// 宾客列表
export const getGuestPaginationList = (data) => {
  return async (dispatch, getState) => {
    dispatch(toggleLoading('guestListLoading'));
    let guestList = [];
    const initialState = getState().guest;
    const { pageSize } = initialState;
    let { pageNum, total } = initialState;
    try {
      const res = await fetchGuestList({ pageNum, pageSize, ...data });
      if (res.code === 0) {
        guestList = get(res, 'data.list', []);
        pageNum = get(res, 'data.pageNum', 1);
        total = get(res, 'data.total', 0);
        // 更新门店可售用户可购卡状态
        guestList = await dispatch(updateAllGuestCanBuyCard(guestList));
      }
    } catch (error) {
      pageNum = 1;
      total = 0;
    } finally {
      guestList = guestList?.map((item) => ({ id: uuid(), ...item }));
      dispatch(toggleLoading('guestListLoading'));
      dispatch({
        type: actionType.GUEST_LIST,
        payload: {
          guestList,
          pageNum,
          total,
        },
      });
    }
  };
};

// 宾客查询
export const queryGuestListByKeyword = (data: { keyWord: string }) => {
  return async (dispatch) => {
    dispatch(toggleLoading('guestListByKeywordLoading'));
    let guestKeywordList = [];
    try {
      const res = await fetchGuestListByKeyword(data);
      if (res?.code === 0) {
        const obj = get(res, 'data', {});
        if (obj) {
          guestKeywordList.push(obj);
        }
        // 更新门店可售用户可购卡状态
        guestKeywordList = await dispatch(updateAllGuestCanBuyCard(guestKeywordList));
      }
    } catch (error) {
      //
    } finally {
      dispatch(toggleLoading('guestListByKeywordLoading'));
      dispatch({
        type: actionType.GUEST_KEYWORD_LIST,
        payload: {
          guestKeywordList,
        },
      });
    }
  };
};

// 宾客列表更新人信息
export const updateSingleCustomer = (data) => {
  return async (dispatch) => {
    dispatch(toggleLoading(''));
    try {
      const res = await fetchUpdateSingleCustomer(data);
      if (res.code === 0) {
        return Promise.resolve();
      }
      dispatch(toggleLoading(''));
      return Promise.reject();
    } catch (error) {
      dispatch(toggleLoading(''));
      return Promise.reject();
    }
  };
};

// 会员卡单价查询
export const queryMemberCardPrice = () => {
  return async (dispatch) => {
    dispatch(toggleLoading(''));
    let price = 0;
    try {
      const res = await fetchQueryMemberCardPrice();
      if (res.code === 0) {
        price = get(res, 'data', 0);
      }
    } catch (error) {
      //
    } finally {
      dispatch(toggleLoading(''));
      dispatch({
        type: actionType.GET_CARD_PRICE,
        payload: {
          memberCardPrice: price,
        },
      });
    }
  };
};

// 购买下单
export const buyMemberCard = (data) => {
  return async (dispatch) => {
    dispatch(toggleLoading('buyCardLoading'));
    let payOrderNo = '';
    try {
      const res = await fetchBuyMemberCard(data);
      if (res.code === 0) {
        payOrderNo = get(res, 'data.payOrderNo', '');
        return Promise.resolve(payOrderNo);
      }
      return Promise.reject();
    } catch (error) {
      //
      return Promise.reject(error);
    } finally {
      dispatch(toggleLoading('buyCardLoading'));
      dispatch({
        type: actionType.BUY_CARD,
        payload: {
          payOrderNo,
        },
      });
    }
  };
};

// 会员卡销售人查询
export const querySalesperson = (data) => {
  return async (dispatch) => {
    dispatch(toggleLoading('querySalespersonLoading'));
    let salespersonList = [];
    try {
      const res = await fetchQuerySalesperson(data);
      if (res.code === 0) {
        salespersonList = get(res, 'data', []);
        return Promise.resolve();
      }
      return Promise.reject();
    } catch (error) {
      //
      return Promise.reject();
    } finally {
      dispatch(toggleLoading('querySalespersonLoading'));
      // 接口挂掉时确保有这个选项
      if (salespersonList?.every((item) => item.saleEmployeeNo !== 'OTHERS')) {
        salespersonList.push({
          saleEmployeeName: renderIntlLabel('guest_buy_card_sale_person_other'),
          saleEmployeeNo: 'OTHERS',
        });
      }
      dispatch({
        type: actionType.QUERY_SALES_PERSON,
        payload: {
          salespersonList,
        },
      });
    }
  };
};

// 手工券列表
export const getManualCouponList = (data) => {
  return async (dispatch) => {
    dispatch(toggleLoading('allManualCouponListLoading'));
    let manualCouponList = [];
    try {
      const res = await fetchManualCouponList(data);
      if (res.code === 0) {
        manualCouponList = get(res, 'data', []);
      }
    } catch (error) {
      //
    } finally {
      dispatch(toggleLoading('allManualCouponListLoading'));
      dispatch({
        type: actionType.COUPON_LIST,
        payload: {
          manualCouponList,
        },
      });
    }
  };
};

// 抖音券列表
export const getDouyinCouponList = (data) => {
  return async (dispatch) => {
    dispatch(toggleLoading('allDouyinCouponListLoading'));
    let douyinCouponList = [];
    try {
      const res = await fetchDouyinCouponList(data);
      if (res.code === 0) {
        douyinCouponList = get(res, 'data', []);
      }
    } catch (error) {
      //
    } finally {
      dispatch(toggleLoading('allDouyinCouponListLoading'));
      dispatch({
        type: actionType.COUPON_LIST,
        payload: {
          douyinCouponList,
        },
      });
    }
  };
};

// 网鱼券列表查询
export const getWangyuCouponList = (data) => {
  return async (dispatch) => {
    dispatch(toggleLoading('allWangyuCouponListLoading'));
    let wangyuCouponList = [];
    try {
      // 1有效期，2金额
      const res = await fetchCouponList({ sortType: 1, ...data });
      if (res.code === 0) {
        wangyuCouponList = get(res, 'data.couponList', []);
      }
    } catch (error) {
      //
    } finally {
      dispatch(toggleLoading('allWangyuCouponListLoading'));
      dispatch({
        type: actionType.COUPON_LIST,
        payload: {
          wangyuCouponList,
        },
      });
    }
  };
};

// 选券弹窗-查询手工券列表
export const getOptionalManualCouponList = (data: { wyuid: string }) => {
  return async (dispatch) => {
    dispatch(toggleLoading('optionalXuanzhuCouponListLoading'));
    let optionalXuanzhuCouponList = [];
    try {
      const res = await fetchManualCouponList(data);
      if (res.code === 0) {
        optionalXuanzhuCouponList = get(res, 'data', []);
        // status === 0 未使用
        optionalXuanzhuCouponList = optionalXuanzhuCouponList?.find((item) => item.status === 0)
          ?.xzCouponList;
      }
    } catch (error) {
      //
    } finally {
      dispatch(toggleLoading('optionalXuanzhuCouponListLoading'));
      dispatch({
        type: actionType.XUANZHU_COUPON_LIST,
        payload: {
          optionalXuanzhuCouponList,
        },
      });
    }
  };
};

// 选券弹窗-抖音券
export const getOptionalDouyinCouponList = (data: { wyuid: string }) => {
  return async (dispatch) => {
    dispatch(toggleLoading('optionalDouyinCouponListLoading'));
    let optionalDouyinCouponList = [];
    try {
      const res = await fetchDouyinCouponList(data);
      if (res.code === 0) {
        optionalDouyinCouponList = get(res, 'data', []);
      }
    } catch (error) {
      //
    } finally {
      dispatch(toggleLoading('optionalDouyinCouponListLoading'));
      dispatch({
        type: actionType.OPTIONAL_DOUYIN_COUPON_LIST,
        payload: {
          optionalDouyinCouponList,
        },
      });
    }
  };
};

// 选券弹窗-网鱼券列表查询
export const getOptionalWangyuCouponList = (data) => {
  return async (dispatch) => {
    dispatch(toggleLoading('optionalWangyuCouponListLoading'));
    let availableCouponList = [];
    try {
      const { wyuid: wyId, ...rest } = data;
      const res = await fetchAvailableCouponList({ wyId, ...rest });
      if (res.code === 0) {
        availableCouponList = get(res, 'data', []);
      }
    } catch (error) {
      //
    } finally {
      dispatch(toggleLoading('optionalWangyuCouponListLoading'));
      dispatch({
        type: actionType.OPTIONAL_WANGYU_COUPON_LIST,
        payload: {
          availableCouponList,
        },
      });
    }
  };
};

export const updateActivityInfo = (record: any, type: 'add' | 'delete', count = 1) => {
  return (dispatch, getState) => {
    const { activityList = [] } = getState().guest;
    const currentIndex = activityList?.findIndex(
      (item) => item.promotionCode === record?.promotionCode,
    );
    if (currentIndex !== -1) {
      const activity = activityList[currentIndex] || {};
      // 限制参与次数
      const { useCount } = activity || {};
      activity.useCount = type === 'add' ? useCount + count : useCount - count;
      activityList[currentIndex] = activity;

      // if (typeof activity.joinCount === 'number' && activity.joinCount >= 0) {
      //   const ruleList = activity.ruleSet?.actionRuleList?.[0]?.conditionRuleList;
      //   const ruleIndex = ruleList?.findIndex(
      //     (subItem) => subItem.code === ComponentCode.JoinLimit,
      //   );
      //   if (ruleIndex !== -1) {
      //     const originalCount = ruleList[ruleIndex].valueList?.[0].value?.[0]?.value?.[0];
      //     if (!isNil(originalCount)) {
      //       const resultCount = type === 'add' ? originalCount + count : originalCount - count;
      //       ruleList[ruleIndex].valueList[0].value[0].value[0] = resultCount;
      //     }
      //     const { label, value: joinCount } = getFormatRulesTextFn(
      //       ruleList?.find((subItem) => subItem.code === ComponentCode.JoinLimit),
      //     );
      //     activity.joinCountText = label;
      //     activity.joinCount = joinCount;
      //     activityList[currentIndex] = activity;
      //   }
      // }
    }
    dispatch(saveData({ activityList }));
  };
};

// 活动列表
export const getActivityList = (data) => {
  return async (dispatch) => {
    dispatch(toggleLoading('activityListLoading'));
    let activityList = [];
    try {
      const res = await fetchActivityList(data);
      if (res.code === 0) {
        const resultList = [];
        const tempAttr = get(res, 'data', []);
        tempAttr.forEach((item) => {
          const resItem = { ...item };
          const { label, value: joinCount } = getFormatRulesTextFn(
            resItem?.ruleSet?.actionRuleList?.[0]?.conditionRuleList?.find(
              (subItem) => subItem.code === ComponentCode.JoinLimit,
            ),
          );
          resItem.joinCountText = label;
          resItem.joinCount = joinCount;
          resItem.conditionOrderAmountFloor = getFormatRulesTextFn(
            resItem?.ruleSet?.actionRuleList?.[0]?.conditionRuleList?.find(
              (subItem) => subItem.code === ComponentCode.RechargeThreshold,
            ),
          )?.value;
          const couponList = resItem?.ruleSet?.actionRuleList?.[1]?.resultRuleList?.filter(
            (subItem) => subItem.code === ResultComponentCode.RechargeContent,
          );
          resItem.couponList = couponList?.map((couponItem) => ({
            couponCode: couponItem?.valueList?.[0],
            couponName: couponItem?.ruleDesc,
            couponCount: couponItem?.valueList?.[1],
          }));
          resultList.push(resItem);
        });
        activityList = resultList;
      }
    } catch (error) {
      //
    } finally {
      dispatch(toggleLoading('activityListLoading'));
      dispatch({
        type: actionType.ACTIVITY_LIST,
        payload: {
          activityList,
        },
      });
    }
  };
};

// 活动下单
export const getActivityOrder = (data) => {
  return async (dispatch) => {
    dispatch(toggleLoading('activityOrderLoading'));
    let activityPayOrderNo = '';
    try {
      const res = await fetchActivityOrder(data);
      if (res.code === 0) {
        activityPayOrderNo = get(res, 'data.payOrderNo', '');
        return Promise.resolve(activityPayOrderNo);
      }
      return Promise.reject();
    } catch (error) {
      //
      return Promise.reject();
    } finally {
      dispatch(toggleLoading('activityOrderLoading'));
      dispatch({
        type: actionType.ACTIVITY_ORDER,
        payload: {
          activityPayOrderNo,
        },
      });
    }
  };
};

// 查询会员相关业务会员卡购买、会员充值是否启用
export const getBusinessPaymentType = (data?) => {
  return async (dispatch) => {
    dispatch(toggleLoading('businessPaymentTypeListLoading'));
    let businessList = [];
    try {
      const res = await fetchBusinessPaymentType(data);
      if (res.code === 0) {
        businessList = get(res, 'data', []);
      }
    } catch (error) {
      //
    } finally {
      dispatch(toggleLoading('businessPaymentTypeListLoading'));
      dispatch({
        type: actionType.BUSINESS_PAYMENT_TYPE_LIST,
        payload: {
          businessList,
        },
      });
    }
  };
};
